import { useState, type ReactNode } from 'react'
import { FormError } from '../common/field-errors'
import { Button } from '@speaking-coach/ui'
import { AdminLoginPage } from './login-page'
import { useAdminAuth } from './use-admin-auth'
import { SIGN_OUT_FAILURE } from './sign-out'

interface RoleGateProps {
  children: ReactNode
}

/**
 * Restrict the admin SPA to users with role=admin.
 * Anyone else (including authenticated students/managers) sees the
 * Unauthorized screen. The api also enforces this via session role
 * checks on every /admin/* endpoint; this gate is UX-only.
 */
export function RoleGate({ children }: RoleGateProps): JSX.Element {
  const { user, logout } = useAdminAuth()
  const [signingOut, setSigningOut] = useState(false)
  const [signOutError, setSignOutError] = useState<string | null>(null)

  if (!user) {
    return <AdminLoginPage />
  }

  if (user.role !== 'admin') {
    return (
      <main
        role="alert"
        aria-live="polite"
        className="min-h-screen flex items-center justify-center bg-surface-app p-8"
      >
        <div className="max-w-md text-center">
          <h1 className="mb-2 font-display text-product-title font-semibold">Access denied</h1>
          <p className="text-text-secondary">
            This area is for Cooori internal admins only.
          </p>
          <Button
            className="mt-5"
            variant="secondary"
            disabled={signingOut}
            onClick={() => {
              setSigningOut(true)
              setSignOutError(null)
              void logout()
                .catch(() => setSignOutError(SIGN_OUT_FAILURE))
                .finally(() => setSigningOut(false))
            }}
          >
            Sign out and try another account
          </Button>
          <FormError message={signOutError} />
        </div>
      </main>
    )
  }

  return <>{children}</>
}
